import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useSearchParams } from 'next/navigation';

import { getRandomInt } from '~utils/numbers';

import {
	CUSTOM_VALUE_QUERY_PARAM_PREFIX,
	CUSTOM_VALUE_QUERY_PARAM_VALUE,
} from '../model/constants';
import {
	isFieldButton,
	isFieldColor,
	isFieldImage,
	isFieldSliders,
	isFieldText,
	isValueMultiSelect,
	isValueSimple,
	isValueSliders,
} from '../model/guards';
import type {
	BotWizardContentDependencyStep,
	BotWizardField,
	BotWizardFieldNames,
	BotWizardStep,
	BotWizardValues,
} from '../model/types';
import { getDependencyContent, getDependencyStep } from '../model/utils';

const getValues = ({
	fields,
	searchParams,
}: {
	fields: Record<BotWizardFieldNames, BotWizardField>;
	searchParams: ReadonlyURLSearchParams;
}): BotWizardValues => {
	const step = searchParams.get('step') ?? '0';
	const path = searchParams.get('path') ?? '0';
	const simpleFieldValuesFromQuery = Object.fromEntries(
		(Object.keys(fields) as BotWizardFieldNames[])
			.filter((key) => isValueSimple(fields[key], {} as BotWizardValues, key))
			.map((key) => {
				const value = searchParams.get(fields[key].queryKey);
				return [key, value ? value.replace(/\+/g, ' ') : undefined];
			})
	);

	const sliderFieldValuesFromQuery = Object.fromEntries(
		(Object.keys(fields) as BotWizardFieldNames[])
			.filter((key) => isValueSliders(fields[key], {} as BotWizardValues, key))
			.map((key) => {
				if (!searchParams.get(fields[key].queryKey)) {
					return [key, undefined];
				}
				const preset = searchParams.get(fields[key].queryKey);
				const field = fields[key];
				if (isFieldSliders(field)) {
					const slidersValues = Object.fromEntries(
						field.data.sliders.map((el) => [
							el.id,
							searchParams.get(field.queryKey + el.queryKey),
						])
					);
					return [
						key,
						{
							preset,
							values: slidersValues,
						},
					];
				}

				return [key, undefined];
			})
	);

	const multiselectFieldValuesFromQuery = Object.fromEntries(
		(Object.keys(fields) as BotWizardFieldNames[])
			.filter((key) =>
				isValueMultiSelect(fields[key], {} as BotWizardValues, key)
			)
			.map((key) => [key, searchParams.getAll(fields[key].queryKey) ?? []])
	);

	return {
		...simpleFieldValuesFromQuery,
		...sliderFieldValuesFromQuery,
		...multiselectFieldValuesFromQuery,
		step,
		path,
	} as BotWizardValues;
};

const validateValues = ({
	fields,
	values,
	firstStep,
	presetSteps,
	customValues,
}: {
	fields: Record<BotWizardFieldNames, BotWizardField>;
	values: BotWizardValues;
	firstStep: BotWizardStep<BotWizardFieldNames[]>;
	presetSteps: BotWizardStep<BotWizardContentDependencyStep>[];
	customValues: { [key in BotWizardFieldNames]?: string };
	searchParams: ReadonlyURLSearchParams;
}) => {
	const newValues = { ...values };
	const processKey = (key: BotWizardFieldNames, isCurrent: boolean) => {
		const field = fields[key];
		if (!field) {
			return null;
		}
		if (isFieldButton(field) || isFieldImage(field) || isFieldColor(field)) {
			const content = getDependencyContent({
				data: field.data,
				contentDependency: field.contentDependency,
				values: newValues,
			});
			if (
				!values[key] ||
				(values[key] &&
					isValueSimple(field, newValues, key) &&
					!content?.find((c) => c.id === values[key]) &&
					(!field.allowCustom ||
						(field.allowCustom &&
							values[key] !== CUSTOM_VALUE_QUERY_PARAM_VALUE &&
							!!customValues[key])))
			) {
				if (field.required) {
					newValues[key] = content?.find((el) => el.default)?.id;
				} else {
					newValues[key] = undefined;
				}
			}
			if (isValueMultiSelect(field, newValues, key)) {
				newValues[key] = (newValues[key] ?? []).filter((value) =>
					content?.find((el) => el.id === value)
				);
			}
		} else if (isFieldText(field)) {
			const content = getDependencyContent({
				data: field.data,
				contentDependency: field.contentDependency,
				values: newValues,
			});
			if (!values[key] && !isCurrent) {
				newValues[key] = content
					? content[getRandomInt(0, content.length - 1)]
					: '';
			}
		} else if (isFieldSliders(field)) {
			const dependencyPresets =
				getDependencyContent({
					data: field.data.presets,
					contentDependency: field.contentDependency,
					values: newValues,
				}) ?? [];
			if (!newValues[key]) {
				newValues[key] = {
					preset: dependencyPresets.find((el) => el.default)?.id || '',
					values: Object.fromEntries(
						field.data.sliders.map((el) => [el.id, '50'])
					),
				};
			}
		}
	};
	const step = parseInt(values.step as string);
	if (values.path !== '0' && values.path !== '1') {
		newValues.path = '0';
	}
	if (
		!Number.isFinite(step) ||
		(values.path === '0' && (step < 0 || step > presetSteps.length + 1)) ||
		(values.path === '1' && (step < 0 || step > 3))
	) {
		newValues.step = '0';
	}
	if (newValues.step === '0') {
		firstStep.data.forEach((el) => processKey(el, true));
	} else if (newValues.path === '0' && newValues.step) {
		firstStep.data.forEach((el) => processKey(el, false));
		Array.from({
			length: Math.min(parseInt(values.step as string), presetSteps.length),
		}).map((_, stepIndex) => {
			const step = getDependencyStep({
				step: presetSteps[stepIndex].data,
				values: newValues,
				contentDependency: ['gender'],
			});
			if (step) {
				step.forEach((el) =>
					processKey(el, (stepIndex + 1).toString() === (values.step as string))
				);
			}
		});
	}
	return newValues;
};

export const getCustomValues = ({
	fields,
	searchParams,
}: {
	fields: Record<BotWizardFieldNames, BotWizardField>;
	searchParams: ReadonlyURLSearchParams;
}) => {
	return Object.fromEntries(
		(Object.keys(fields) as BotWizardFieldNames[])
			.filter((key) => fields[key].allowCustom)
			.map((key) => [
				key,
				searchParams.get(
					CUSTOM_VALUE_QUERY_PARAM_PREFIX + fields[key].queryKey
				) ?? undefined,
			])
	) as { [key in BotWizardFieldNames]?: string };
};

export const validateCustomValues = ({
	fields,
	customValues,
}: {
	fields: Record<BotWizardFieldNames, BotWizardField>;
	customValues: { [key in BotWizardFieldNames]?: string };
}) => {
	const newCustomValues = { ...customValues };
	(Object.keys(fields) as BotWizardFieldNames[])
		.filter((key) => fields[key].isAgeCustom)
		.forEach((key) => {
			if (newCustomValues[key]) {
				const parsedInt = parseInt(customValues[key] as string);
				if (
					!Number.isFinite(parsedInt) ||
					parsedInt < 18 ||
					parsedInt >= 10000
				) {
					newCustomValues[key] = undefined;
				}
			}
		});
	return newCustomValues;
};

export const useQueryData = ({
	fields,
	firstStep,
	presetSteps,
}: {
	firstStep: BotWizardStep<BotWizardFieldNames[]>;
	presetSteps: BotWizardStep<BotWizardContentDependencyStep>[];
	fields: Record<BotWizardFieldNames, BotWizardField>;
}) => {
	const searchParams = useSearchParams();
	const customValues = getCustomValues({ fields, searchParams });
	const validatedCustomValues = validateCustomValues({
		fields,
		customValues,
	});
	const values = getValues({ fields, searchParams });
	const validatedValues = validateValues({
		fields,
		values,
		firstStep,
		presetSteps,
		searchParams,
		customValues,
	});

	return { values: validatedValues, customValues: validatedCustomValues };
};
